import {
  AloftDate,
  AloftDateJSON,
  CompType,
  CompsListingsSearchCriteria,
  LocationType,
  MLSListing,
  PropertyData,
  RecommendedCompsStatistics,
  SubjectData,
  isDefined,
} from '@aloft/toolkit-shared';
import { Block, Flex, Modal } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { useDisclosure } from '@aloft/toolkit-sui/chakra';
import { SortingState } from '@tanstack/react-table';

import { SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { useAutofillProperty } from '../../hooks/useAutofillProperty.ts';
import { dismissAlertToasts } from '../../hooks/useToaster/dismissAllToasts.ts';
import { AddComparablesModal } from '../../modules/analysis/modals/addcomparables/AddComparablesModal.tsx';
import { removeComps } from '../../redux/features/actions/removeComp.ts';
import {
  setReferenceViewOpen,
  setSelectedDataSheet,
} from '../../redux/features/referenceBarUISlice.ts';
import { featuresSelectors } from '../../redux/features/selectors/featuresSelectors.ts';
import {
  selectDefaultFeatureAreas,
  selectOrder,
  selectUadToCraftsmanQuality,
} from '../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { getLocationTypeProximityRequirementMiles } from '../../utils/locationTypeProximityRequirementMiles.ts';
import { getLocationTypeSaleDateRequirementMonths } from '../../utils/locationTypeSaleDateRequirementMonths.ts';
import { LoadingScreenSam } from '../LoadingScreen/LoadingScreenSam.tsx';
import { LoadingScreenSamFull } from '../LoadingScreen/LoadingScreenSamFull.tsx';
import { Tkv2ReferenceBar } from '../ReferenceBar/Tkv2ReferenceBar.tsx';
import {
  RecCompSelectionProvider,
  RecCompSelectionReferenceViewDrawer,
} from './RecCompSelectionContext.tsx';
import { RecommendedCompsModalHeader } from './RecommendedCompsModalHeader.tsx';
import { useCompTableColumns } from './hooks/useCompTableColumns.ts';
import { useRecommendedCompsFilterRoute } from './hooks/useRecommendedCompsFilterRoute.ts';
import { useRecommendedCompsInternalState } from './hooks/useRecommendedCompsInternalState.ts';
import { useRecommendedProperties } from './hooks/useRecommendedProperties.ts';
import { useSimilarityScoreWeightingModal } from './hooks/useSimilarityScoreWeightingModal.tsx';
import { useSimilarityScoreWeights } from './hooks/useSimilarityScoreWeights.ts';
import { useSubjectPropertyToCompSelectionProperty } from './hooks/useSubjectPropertyToCompSelectionProperty.ts';
import { ColumnSettingsModal } from './internal/ColumnSettingsModal.tsx';
import {
  dismissDefaultFiltersInfo,
  isDefaultFiltersInfoDismissed,
} from './internal/FilterDrawer/defaultFiltersInfoToastStorage.ts';
import { DefaultFiltersInfoToast } from './internal/FilterPanel/DefaultFiltersInfoToast.tsx';
import { FilterPanel } from './internal/FilterPanel/FilterPanel.tsx';
import { HiddenCompsWarningToast } from './internal/FilterPanel/HiddenCompsWarningToast.tsx';
import { getPropertyTypeFilterValue } from './internal/FilterPanel/propertyTypeOptions.ts';
import { RecommendedCompSelection } from './internal/RecommendedCompSelection.tsx';
import { QualityTierBreakdown } from './internal/qualityTier/QualityTierBreakdown.tsx';
import {
  CompSelectionProperty,
  CompSelectionState,
  FiltersMessage,
  QualityTierBreakdownData,
} from './types.ts';
import { commitAppliedCompSearch } from './utils/compSearchUtils.ts';
import { getSelectedIDs } from './utils/compSelectionState.ts';
import {
  compTableColumnsToTableState,
  tableStateToCompTableColumns,
} from './utils/compTableColumnsToTableState.ts';
import { compsToCompSelectionProps } from './utils/compsToCompSelectionProps.ts';
import {
  didServerCompSearchChange,
  getChangedSearchFilterLabels,
} from './utils/getChangedSearchFilterLabels.ts';
import {
  DEFAULT_RECOMMENDED_COMP_PROPERTY_SUB_TYPE,
  getDefaultGlaPercentage,
  getDefaultSiteSizePercentage,
} from './utils/getDefaultRecommendedCompSearch.ts';
import { handleAddClickForCompCreation } from './utils/handleAddClickForCompCreation.ts';
import { handleAddClickZillow } from './utils/handleAddClickZillow.ts';
import { loadExistingSearchCriteria } from './utils/loadExistingSearchCriteria.ts';

const TOP_COMPS_WARNING_COUNT = 20;

const { selectShouldShowRecommendedCompsFiltering, selectShouldAllowNewSimilarityScore } =
  featuresSelectors;

type RecommendedCompsModalProps = {
  compType?: 'sales' | 'rental';
  createComps: (props: PropertyData[]) => void;
  currentComps: Record<string, PropertyData>;
  compSeq: string[];
  orderID: string;
  isModalOpen: boolean;
  onModalClose: () => void;
  subject: SubjectData;
  effectiveDate: AloftDateJSON;
};

export const RecommendCompsModal = ({
  compType = 'sales',
  createComps,
  currentComps,
  compSeq,
  orderID,
  isModalOpen,
  onModalClose,
  subject,
  effectiveDate,
}: RecommendedCompsModalProps) => {
  const isRental = compType === 'rental';

  const dispatch = useDispatch();
  const recommendedProperties = useRecommendedProperties({ orderID, compType });
  const { isLoading, data, getProperties, isError } = recommendedProperties;

  const { hasArvOption, isInspectifyOrder } = useAppSelector(selectOrder);
  const defaultFeatureAreas = useAppSelector(selectDefaultFeatureAreas);
  const uadToCraftsmanQuality = useAppSelector(selectUadToCraftsmanQuality);
  const allowNewSimilarityScore = useAppSelector(selectShouldAllowNewSimilarityScore);
  const autofillProperty = useAutofillProperty();
  const { SimilarityScoreWeightingModal, openSimilarityScoreWeightingModal } =
    useSimilarityScoreWeightingModal();

  const [isAddCompsLoading, setIsAddCompsLoading] = useState(false);
  const [isManualAddModalOpen, setIsManualAddModalOpen] = useState(false);
  const [newFiltersApplied, setNewFiltersApplied] = useState(false);
  const [filtersMessage, setFiltersMessage] = useState<FiltersMessage | undefined>(undefined);
  const [allowApplyNewFilters, setAllowApplyNewFilters] = useState(false);
  const [hiddenCompsWarning, setHiddenCompsWarning] = useState<
    { count: number; changedFilterLabels: string[] } | undefined
  >(undefined);
  const previousTopCompIdsRef = useRef<string[]>([]);
  const changedFilterLabelsRef = useRef<string[]>([]);

  const ref: React.RefObject<{
    downloadCSV: () => void;
    getQualityTiersBreakdown: () => QualityTierBreakdownData;
    getRowsWithUADRatings: () => CompSelectionProperty[];
    resolveManualComp: (property: CompSelectionProperty, compType?: CompType) => string;
  } | null> = useRef(null);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<CompSelectionState>({});
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);
  const previousDataRef = useRef<typeof data>();
  const hasInitializedFormFromDataRef = useRef(false);
  const [qualityTierBreakdownData, setQualityTierBreakdownData] =
    useState<QualityTierBreakdownData>();

  const handleQualityTierBreakdownDataChange = useCallback((d: QualityTierBreakdownData) => {
    setQualityTierBreakdownData(d);
  }, []);

  const existingSearchCriteria: CompsListingsSearchCriteria | undefined =
    data?.body?.output?.searchCriteria;

  const [statistics, setStatistics] = useState<RecommendedCompsStatistics>();

  const featureAllowRecommendedCompsFiltering = useAppSelector(
    selectShouldShowRecommendedCompsFiltering,
  );

  const allowRecommendedCompsFiltering = featureAllowRecommendedCompsFiltering || isInspectifyOrder;

  const subjectLocationType: LocationType =
    subject.editableData.propertyIdentification.locationType ?? 'Urban';

  const [showDefaultFiltersInfo, setShowDefaultFiltersInfo] = useState(
    !isDefaultFiltersInfoDismissed(orderID),
  );
  const propertyTypeLabel =
    getPropertyTypeFilterValue(subject.editableData.improvements.propertyType) ??
    DEFAULT_RECOMMENDED_COMP_PROPERTY_SUB_TYPE;
  const proximityRequirementMiles = getLocationTypeProximityRequirementMiles(subjectLocationType);
  const saleDateRequirementMonths = getLocationTypeSaleDateRequirementMonths(subjectLocationType);
  const glaPercentage = getDefaultGlaPercentage(subjectLocationType);
  const siteSizePercentage = getDefaultSiteSizePercentage(subjectLocationType);

  const { weights: similarityScoreWeights } = useSimilarityScoreWeights(orderID);

  const {
    columns: compTableColumns,
    save: saveCompTableColumns,
    isSaving: isSavingCompTableColumns,
  } = useCompTableColumns(orderID);

  const { columnOrder, columnVisibility } = useMemo(
    () => compTableColumnsToTableState(compTableColumns),
    [compTableColumns],
  );

  const handleColumnOrderChange = useCallback(
    (nextOrder: string[]) => {
      void saveCompTableColumns(tableStateToCompTableColumns(nextOrder, compTableColumns), true);
    },
    [compTableColumns, saveCompTableColumns],
  );

  const { form, showNoRecommendations, setShowNoRecommendations, getDefaultFormValues } =
    useRecommendedCompsInternalState(subject, AloftDate.schema.parse(effectiveDate));
  const effectiveDateAloft = useMemo(
    () => AloftDate.fromIsoString(effectiveDate.isoString),
    [effectiveDate.isoString],
  );
  const subjectComp = useSubjectPropertyToCompSelectionProperty(subject, effectiveDateAloft);

  const currentCompsSet = useMemo(
    (): Record<string, string> =>
      Object.fromEntries(
        compSeq.flatMap((compId) => {
          const comp = compsToCompSelectionProps([compId], currentComps, subjectComp).find(
            isDefined,
          );
          return comp ? [[comp.id, compId]] : [];
        }),
      ),
    [compSeq, currentComps, subjectComp],
  );

  useEffect(() => {
    if (!isModalOpen) return;

    dismissAlertToasts();
    dispatch(setSelectedDataSheet('mls-map'));
    dispatch(setReferenceViewOpen(true));
  }, [isModalOpen]);

  const { setValue } = form;
  useEffect(() => {
    if (!isModalOpen) {
      hasInitializedFormFromDataRef.current = false;
      previousDataRef.current = undefined;
      return;
    }
    if (data && !isError && !hasInitializedFormFromDataRef.current) {
      hasInitializedFormFromDataRef.current = true;
      previousDataRef.current = data;

      const search = existingSearchCriteria
        ? loadExistingSearchCriteria(existingSearchCriteria, subject, getDefaultFormValues)
        : getDefaultFormValues();
      setValue('search', search);
      setValue('originalSearch', search);
    }
  }, [isModalOpen, data, isError, existingSearchCriteria, subject, getDefaultFormValues, setValue]);

  useEffect(() => {
    if (!data || isError) return;
    const currentCompsKeys = Object.keys(currentCompsSet || {});
    if (currentCompsKeys.length > 0) {
      if (hasArvOption && isInspectifyOrder) {
        setRowSelection((prevSelection) => {
          const newSelection: CompSelectionState = { ...prevSelection };

          currentCompsKeys.forEach((listingId) => {
            if (hasInitializedSelection && newSelection[listingId]) return;
            const compId = currentCompsSet[listingId];
            const comp = compId ? currentComps[compId] : undefined;
            if (comp) {
              const existing = newSelection[listingId] || {};
              newSelection[listingId] = {
                aiv: existing.aiv || comp.compType === 'aiv',
                arv: existing.arv || comp.compType === 'arv',
              };
            }
          });

          return newSelection;
        });
      } else if (!hasInitializedSelection) {
        setRowSelection(Object.fromEntries(currentCompsKeys.map((id) => [id, {}])));
      }
      setHasInitializedSelection(true);
    }
  }, [
    data,
    isError,
    currentCompsSet,
    currentComps,
    hasInitializedSelection,
    isInspectifyOrder,
    hasArvOption,
  ]);

  useEffect(() => {
    const subscribe = form.subscribe({
      formState: {
        values: true,
      },
      callback: (state) => {
        const stateChangedFromOriginal =
          JSON.stringify(state.values.originalSearch) !== JSON.stringify(state.values.search);

        const { geoSearch } = state.values.search;
        const geoSearchValid =
          (geoSearch.t === 'polygon' && geoSearch.coords.length > 0) ||
          (geoSearch.t === 'radius' && geoSearch.radiusInMeters > 0);
        setAllowApplyNewFilters(stateChangedFromOriginal && geoSearchValid);
      },
    });

    return subscribe;
  }, [form]);

  const { isFilterDrawerOpen, onFilterDrawerOpen, onFilterDrawerClose } =
    useRecommendedCompsFilterRoute();

  const {
    isOpen: isQualityTierBreakdownOpen,
    onOpen: onQualityTierBreakdownOpen,
    onClose: onQualityTierBreakdownClose,
  } = useDisclosure();

  const {
    isOpen: isColumnSettingsOpen,
    onOpen: onColumnSettingsOpen,
    onClose: onColumnSettingsClose,
  } = useDisclosure();

  const handleFilterDrawerClose = useCallback(() => {
    onFilterDrawerClose();
    form.setValue('search', form.getValues('originalSearch'));
  }, [form, onFilterDrawerClose]);

  const toggleFilterPanel = useCallback(() => {
    if (!allowRecommendedCompsFiltering) return;
    if (isFilterDrawerOpen) {
      handleFilterDrawerClose();
    } else {
      onFilterDrawerOpen();
    }
  }, [
    allowRecommendedCompsFiltering,
    handleFilterDrawerClose,
    isFilterDrawerOpen,
    onFilterDrawerOpen,
  ]);

  const openQualityTierBreakdown = useCallback(() => {
    setQualityTierBreakdownData(ref.current?.getQualityTiersBreakdown());
    onQualityTierBreakdownOpen();
  }, [onQualityTierBreakdownOpen]);

  const recCompSelectionProviderValue = useMemo(
    () => ({
      openSimilarityScoreWeightingModal,
      openColumnSettings: onColumnSettingsOpen,
      openQualityTierBreakdown,
      openFilterDrawer: toggleFilterPanel,
      isFilterPanelOpen: isFilterDrawerOpen,
    }),
    [
      isFilterDrawerOpen,
      onColumnSettingsOpen,
      openQualityTierBreakdown,
      openSimilarityScoreWeightingModal,
      toggleFilterPanel,
    ],
  );

  const applyFilters = () => {
    const { originalSearch, appliedSearch } = commitAppliedCompSearch(form);
    previousTopCompIdsRef.current = (ref.current?.getRowsWithUADRatings() ?? [])
      .slice(0, TOP_COMPS_WARNING_COUNT)
      .map((row) => row.id);
    changedFilterLabelsRef.current = getChangedSearchFilterLabels(originalSearch, appliedSearch);
    setShowNoRecommendations(false);
    if (didServerCompSearchChange(originalSearch, appliedSearch)) {
      getProperties({ search: appliedSearch });
    }
    setNewFiltersApplied(true);
    setFiltersMessage(undefined);
    setHiddenCompsWarning(undefined);
    setAllowApplyNewFilters(false);
    dismissDefaultFiltersInfo(orderID);
    setShowDefaultFiltersInfo(false);
  };

  useEffect(() => {
    if (isLoading) return;

    if (isError || data?.body?.status === 'error') {
      setFiltersMessage({
        type: 'error',
        message: 'Failed to load recommended comps. Please try again or update your filters.',
        color: 'alertError',
        icon: 'warning',
      });
    } else if (!newFiltersApplied) {
      setFiltersMessage(undefined);
    } else if (data?.body?.status === 'not_found') {
      setFiltersMessage({
        type: 'warning',
        message: 'No comps found with the current filters. Try broadening your search criteria.',
        color: 'statusWarning',
        icon: 'warning',
      });
    } else if (newFiltersApplied && !allowApplyNewFilters) {
      setFiltersMessage({
        type: 'success',
        message: 'Filters applied!',
        color: 'alertSuccess',
        icon: 'check',
      });

      const previousTopCompIds = previousTopCompIdsRef.current;
      if (previousTopCompIds.length > 0) {
        const newTopCompIds = new Set(
          (ref.current?.getRowsWithUADRatings() ?? [])
            .slice(0, TOP_COMPS_WARNING_COUNT)
            .map((row) => row.id),
        );
        const missingCompIds = previousTopCompIds.filter((id) => !newTopCompIds.has(id));
        previousTopCompIdsRef.current = [];
        setHiddenCompsWarning(
          missingCompIds.length > 0
            ? { count: missingCompIds.length, changedFilterLabels: changedFilterLabelsRef.current }
            : undefined,
        );
      }
    } else {
      setFiltersMessage(undefined);
    }
  }, [data, isLoading, newFiltersApplied, isError, allowApplyNewFilters]);

  const handleSetRowSelection = (newSet: SetStateAction<CompSelectionState>) => {
    setRowSelection((prev) => {
      const resolvedValue = typeof newSet === 'function' ? newSet(prev) : newSet;
      if (data?.body?.status !== 'found') return resolvedValue;

      const { results } = data.body!.output;
      Object.keys(resolvedValue).forEach((key) => {
        const selection = resolvedValue[key];
        if (selection && !selection.listingData) {
          const listing = results.find(
            (result) =>
              ('ToolkitListingID' in result && result.ToolkitListingID === key) ||
              ('ListingId' in result && result.ListingId === key),
          );

          if (listing) selection.listingData = listing as MLSListing;
        }
      });
      return resolvedValue;
    });
  };

  // Properties resolved via the "Manually Add Comps" modal, keyed by the id they were
  // assigned in the table (see resolveManualComp). These are already full PropertyData
  // objects (autofilled from MLS/address), so on "Update comps" they're added directly
  // instead of being routed through the recommendation-listing autofill path, which
  // only knows how to build PropertyData from a matching row's `listingData`.
  const manualPropertiesByIdRef = useRef<Record<string, PropertyData>>({});

  // The comp table (and its `resolveManualComp` imperative handle) only mounts once
  // `showNoRecommendations` is false. If the user manually adds a comp while the
  // "No Recommended Comps Found" screen is showing, `ref.current` isn't set yet, so we
  // queue the properties here and flush them once the table mounts (see effect below).
  const [pendingManualProperties, setPendingManualProperties] = useState<PropertyData[]>([]);

  const resolveManualProperties = useCallback(
    (properties: PropertyData[]) => {
      properties.forEach((property, index) => {
        const compSelectionProp = compsToCompSelectionProps(
          [`manual-${index}`],
          { [`manual-${index}`]: property },
          subjectComp,
        ).find(isDefined);
        if (!compSelectionProp) return;
        const id = ref.current?.resolveManualComp(compSelectionProp, property.compType);
        if (id) manualPropertiesByIdRef.current[id] = property;
      });
    },
    [subjectComp],
  );

  useEffect(() => {
    if (showNoRecommendations || pendingManualProperties.length === 0) return;
    resolveManualProperties(pendingManualProperties);
    setPendingManualProperties([]);
  }, [showNoRecommendations, pendingManualProperties, resolveManualProperties]);

  const handleManualPropertiesResolved = (properties: PropertyData[]) => {
    if (showNoRecommendations) {
      // Table isn't mounted yet - reveal it, then resolve the comps once it is (see effect above).
      setShowNoRecommendations(false);
      setPendingManualProperties((prev) => [...prev, ...properties]);
    } else {
      resolveManualProperties(properties);
    }
    setIsManualAddModalOpen(false);
  };

  const dismissModal = () => {
    setRowSelection({});
    setHasInitializedSelection(false);
    setHiddenCompsWarning(undefined);
    manualPropertiesByIdRef.current = {};
    dispatch(setReferenceViewOpen(false));
    onModalClose();
  };

  const saveComps = async () => {
    setIsAddCompsLoading(true);
    try {
      const selectedIDs = getSelectedIDs(rowSelection, isInspectifyOrder, hasArvOption);
      const compsToAdd = selectedIDs.filter((id) => !currentCompsSet[id] && id !== 'subject');

      const manualCompsToAdd = compsToAdd
        .map((id) => manualPropertiesByIdRef.current[id])
        .filter(isDefined);
      if (manualCompsToAdd.length > 0) {
        createComps(manualCompsToAdd);
      }

      if (data?.body?.status !== 'found') {
        dismissModal();
        return;
      }

      const rowsWithUADRatings = Object.fromEntries(
        (ref.current?.getRowsWithUADRatings() ?? []).map((row) => [row.id, row]),
      );

      const newRowSelection: CompSelectionState = Object.fromEntries(
        compsToAdd
          .filter((id) => !manualPropertiesByIdRef.current[id])
          .map((id) => {
            const row = rowsWithUADRatings[id];
            return [
              id,
              {
                ...rowSelection[id],
                uadQualityRating: row?.uadQualityRating,
                uadConditionRating: row?.uadConditionRating,
              },
            ];
          }),
      );

      const compsToDelete = Object.keys(currentCompsSet)
        .filter((id) => !selectedIDs.includes(id) && id !== 'subject')
        .map((id) => currentCompsSet[id])
        .filter(isDefined);

      if (compsToDelete.length > 0) {
        dispatch(
          removeComps(
            isRental
              ? { ids: compsToDelete, sequenceTarget: 'rentalCompSequence' }
              : { ids: compsToDelete },
          ),
        );
      }

      if (isRental || data.source === 'abode') {
        await handleAddClickForCompCreation({
          isLoading,
          rowSelection: newRowSelection,
          createComps,
          autofillProperty,
          effectiveDate,
          orderID,
          isInspectifyOrder,
          hasArvOption,
          defaultFeatureAreas: defaultFeatureAreas ?? undefined,
          uadToCraftsmanQuality: uadToCraftsmanQuality ?? undefined,
          ...(isRental && { listingType: 'rentalListing' }),
        });
      } else if (data.source === 'z') {
        await handleAddClickZillow({
          data: data.body.output.results,
          isLoading,
          rowSelection: newRowSelection,
          createComps,
          autofillProperty,
          effectiveDate,
          orderID,
          isInspectifyOrder,
          hasArvOption,
        });
      }

      dismissModal();
    } catch (e: unknown) {
      console.error(
        e,
        isRental ? 'Failed to add recommended rental comps.' : 'Failed to add recommended comps.',
      );
      throw e;
    } finally {
      setIsAddCompsLoading(false);
    }
  };

  const loadingScreenActive = isAddCompsLoading || (isLoading && !isFilterDrawerOpen);

  return (
    <FormProvider {...form}>
      <RecCompSelectionProvider value={recCompSelectionProviderValue}>
        <Modal
          size="full"
          isOpen={isModalOpen}
          onClose={dismissModal}
          closeOnEsc
          showCloseButton
          fullHeight
          modalBodyProps={{ padding: 0, overflow: 'hidden' }}
          title={
            <RecommendedCompsModalHeader
              statistics={statistics}
              isLoading={loadingScreenActive}
              onExportCSV={() => ref.current?.downloadCSV()}
              onSettingsClick={onColumnSettingsOpen}
              onManuallyAddComps={() => setIsManualAddModalOpen(true)}
            />
          }
        >
          <AddComparablesModal
            isModalOpen={isManualAddModalOpen}
            onModalClose={() => setIsManualAddModalOpen(false)}
            currentComps={currentComps}
            compSequence={compSeq}
            listingType={isRental ? 'rentalListing' : undefined}
            onPropertiesResolved={handleManualPropertiesResolved}
          />

          <ColumnSettingsModal
            isOpen={isColumnSettingsOpen}
            onClose={onColumnSettingsClose}
            columns={compTableColumns}
            save={saveCompTableColumns}
            isSaving={isSavingCompTableColumns}
          />

          {SimilarityScoreWeightingModal}

          <QualityTierBreakdown
            isOpen={isQualityTierBreakdownOpen}
            onClose={onQualityTierBreakdownClose}
            qualityTierBreakdownData={qualityTierBreakdownData}
            selectedIds={getSelectedIDs(rowSelection, isInspectifyOrder, hasArvOption).filter(
              (id) => id !== 'subject',
            )}
          />

          <Block position="relative" width="100%" height="100%">
            <LoadingScreenSamFull
              isLoading={loadingScreenActive}
              what="comps"
              wrapperProps={{ backgroundColor: 'surface1', top: '0', zIndex: 1201 }}
            />
            <Flex height="100%" width="100%">
              {allowRecommendedCompsFiltering && isFilterDrawerOpen && (
                <FilterPanel
                  subject={subject}
                  isLoading={isLoading}
                  allowApplyNewFilters={allowApplyNewFilters}
                  orderID={orderID}
                  filtersMessage={filtersMessage}
                  onCancel={handleFilterDrawerClose}
                  onApply={applyFilters}
                />
              )}
              <Block position="relative" flex={1} minWidth={0} height="100%">
                <RecCompSelectionReferenceViewDrawer>
                  <RecommendedCompSelection
                    compType={compType}
                    orderID={orderID}
                    subject={subject}
                    comps={currentComps}
                    compSeq={compSeq}
                    effectiveDate={effectiveDate}
                    sorting={sorting}
                    setSorting={setSorting}
                    rowSelection={rowSelection}
                    setRowSelection={handleSetRowSelection}
                    currentComps={currentCompsSet}
                    showNoRecommendations={showNoRecommendations}
                    setShowNoRecommendations={setShowNoRecommendations}
                    getDefaultFormValues={getDefaultFormValues}
                    isInspectifyOrder={isInspectifyOrder}
                    hasArvOption={hasArvOption}
                    allowRecommendedCompsFiltering={allowRecommendedCompsFiltering}
                    onClose={dismissModal}
                    allowNewSimilarityScore={allowNewSimilarityScore}
                    similarityScoreWeights={similarityScoreWeights}
                    updateCompsLabel={isRental ? 'Update Rental Comps' : 'Update comps'}
                    onUpdateComps={saveComps}
                    onDeselectAll={() => setRowSelection({})}
                    onFilterOpen={toggleFilterPanel}
                    onColumnOrderChange={handleColumnOrderChange}
                    columnOrder={columnOrder}
                    columnVisibility={columnVisibility}
                    isFilterPanelOpen={isFilterDrawerOpen}
                    proximityRequirementMiles={proximityRequirementMiles}
                    onQualityTierBreakdownDataChange={handleQualityTierBreakdownDataChange}
                    onStatisticsChange={setStatistics}
                    ref={ref}
                  />
                </RecCompSelectionReferenceViewDrawer>
                {isLoading && isFilterDrawerOpen && (
                  <Block
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    zIndex={20}
                    backgroundColor="surface1"
                  >
                    <LoadingScreenSam overrideText="Applying filters... this might take a few seconds." />
                  </Block>
                )}
                {allowRecommendedCompsFiltering &&
                  isFilterDrawerOpen &&
                  showDefaultFiltersInfo &&
                  !isLoading && (
                    <DefaultFiltersInfoToast
                      orderID={orderID}
                      propertyTypeLabel={propertyTypeLabel}
                      proximityRequirementMiles={proximityRequirementMiles}
                      saleDateRequirementMonths={saleDateRequirementMonths}
                      glaPercentage={glaPercentage}
                      siteSizePercentage={siteSizePercentage}
                      onDismiss={() => setShowDefaultFiltersInfo(false)}
                    />
                  )}
                {hiddenCompsWarning && !isLoading && (
                  <HiddenCompsWarningToast
                    count={hiddenCompsWarning.count}
                    changedFilterLabels={hiddenCompsWarning.changedFilterLabels}
                    onDismiss={() => setHiddenCompsWarning(undefined)}
                  />
                )}
              </Block>
              <Tkv2ReferenceBar />
            </Flex>
          </Block>
        </Modal>
      </RecCompSelectionProvider>
    </FormProvider>
  );
};
